import React, { useEffect, useReducer, useState } from "react";
import axios from "axios";
import { Tabs, Tab, Paper, Tooltip } from "@mui/material";
import { styled } from "@mui/system";
import PullListForm from "./PullListForm";
import BASE_URL from "../../config/apiConfig";

const RootContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
}));

const TabsContainer = styled("div")(() => ({
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginBottom: 0,
}));

const FormContainer = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    width: "98.2%",
    marginBottom: theme.spacing(2),
}));

const FormBox = styled(Paper)(({ theme }) => ({
    width: "100%",
    padding: theme.spacing(2),
    border: "1px solid #ccc",
    borderRadius: 0,
    boxShadow: "none",
    display: "flex",
    flexDirection: "column",
}));

const StyledTabs = styled(Tabs)(() => ({
    backgroundColor: "#F0F0F0",
    margin: 0,
    "& .MuiTabs-indicator": {
        backgroundColor: "#1976D2",
    },
    "& .MuiTabs-root": {
        justifyContent: "flex-start",
    },
    "& .Mui-selected": {
        color: "#fff",
        backgroundColor: "#ADADAD",
    },
    "& .MuiTab-root": {
        "&:hover": {
            backgroundColor: "#ADD8E6",
        },
    },
}));

const TabLabel = styled("span")(({ theme }) => ({
    textTransform: "none",
    padding: theme.spacing(1, 2),
    cursor: "pointer",
}));

const StatusTabLabel = ({ label, status }) => {
    const color = STATUS_COLORS[status] || "gray";

    return (
        <Tooltip
            arrow
            placement="top"
            title={
                <>
                    {status && <div>{status}</div>}
                </>
            }
        >
            <TabLabel>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {label}
                    {status !== "Withdrawn" && (
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                borderRadius: "50%",
                                backgroundColor: color
                            }}
                        />
                    )}
                </span>
            </TabLabel>
        </Tooltip>
    );
};

const formsReducer = (state, action) => {
    switch (action.type) {
        case "ADD_NEW_FORM":
            return [...state, action.newForm];
        case "UPDATE_FORM":
            const updatedState = [...state];
            updatedState[action.formIndex] = action.updatedForm;
            return updatedState;
        case "DELETE_FORM":
            return state.filter((_, index) => index !== action.formIndex);
        default:
            return state;
    }
};

const STATUS_COLORS = {
    "Pull List Development": "red",
    "Pending Pull List Approval": "red",
    "Pull List Approved": "orange",
    "Pull List Executed": "orange",
    "Submitted to Repository": "orange",
    "Results Data Processing": "yellow",
    "Pending Follow-up": "yellow",
    "Completed": "green",
    "Real Time": "blue",
    "Withdrawn": "transparent"
};

const PullListTable = ({ epId, pullListFolderId, sampleDisposal, lab, labLegacyAddress, couriers, isEPApproved }) => {
    const defaultData = {
        id: 0,
        epId: epId,
        pullListId: 0,
        version: 1,
        pullListFile: "",
        pullListFileVersion: 0,
        sampleQualityFile: "",
        sampleQualityFileVersion: 0,
        disposalRequestFile: "",
        disposalRequestFileVersion: 0,
        status: "Pull List Development",
        fileLoadStatus: null,
        shipmentFromRepo: (sampleDisposal === "Yes" ? "Sample Disposal" : "Yes"),
        rushOrder: false,
        disposalReason: null,
        pullListComment: null,
        numberOfSamples: null,
        shippingTemperature: null,
        dispositionOfSample: null,
        informedConsent: "No",
        informedConsentApprover: "No",
        informedConsentComment: null,
        approvalBy: null,
        approvalDate: null,
        courier: null,
        trackingNumber: null,
        serviceRequestNumber: null,
        submitDate: null,
        shipDate: null,
        labName: null,
        labDesc: null,
        labContact: null,
        labPhoneNumber: null,
        labEmail: null,
        labPI: null,
        labAddress1: null,
        labAddress2: null,
        labAddress3: null,
        labAddress4: null,
        labCity: null,
        labState: null,
        labPostalCode: null,
        labLegacyAddress: null,
        expectedDataDate: null,
        resultsReceivedDate: null,
        resultsValidatedDate: null,
        validationStatus: "Awaiting Data",
        validationBy: null,
        validationNotesText: null,
        datasetsValidationLink: null,
        relatedJiraTickets: "",
        epUpdate: null,
        boxId: "",
        boxFilesId: "",
        repoComplete: false,
        createdAt: null,
        updatedAt: null,
        updatedBy: null,
    };

    const [loading, setLoading] = useState(true);
    const [tabLabels, setTabLabels] = useState([]);
    const [activeTabIndex, setActiveTabIndex] = useState(0);
    const [forms, dispatchForms] = useReducer(formsReducer, []);

    const fetchData = async () => {
        try {
            const maxPullListId = await axios.get(BASE_URL + `/api/pullLists/count/${epId}`, { withCredentials: true }).then((response) => response.data.maxPullListId);

            if (maxPullListId > 0) {
                const formsData = [];

                for (let id = 1; id <= maxPullListId; id++) {
                    const response = await axios.get(BASE_URL + `/api/pullLists/ep/${epId}/pl/${id}`, { withCredentials: true });
                    formsData.push({
                        isNewSeries: false,
                        pullListFile: null,
                        sampleQualityFile: null,
                        disposalRequestFile: null,
                        data: response.data[0],
                        pullLists: response.data,
                    });
                }

                formsData.forEach((newForm) => {
                    dispatchForms({ type: "ADD_NEW_FORM", newForm: newForm });
                });

                setActiveTabIndex(maxPullListId - 1);
                setTabLabels([...Array(maxPullListId).keys()].map((i) => (i + 1).toString()));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }

    useEffect(() => {
        fetchData().then(() => {
            setLoading(false);
        });
    }, []);

    const handleTabClick = (index) => {
        setActiveTabIndex(index);
    };

    const addNewForm = () => {
        const length = tabLabels.length;
        setTabLabels([...tabLabels, `${length + 1}`]);
        const newForm = {
            isNewSeries: true,
            pullListFile: null,
            sampleQualityFile: null,
            disposalRequestFile: null,
            data: { ...defaultData, pullListId: 1 + length },
            pullLists: [],
        };
        dispatchForms({ type: "ADD_NEW_FORM", newForm: newForm });
        setActiveTabIndex(length);
    };

    const handleCancelNewForm = () => {
        if (tabLabels.length > 0
            && forms.length > 0
            && forms[forms.length - 1].isNewSeries
        ) {
            dispatchForms({ type: "DELETE_FORM", formIndex: forms.length - 1 });
            setTabLabels((prevLabels) => prevLabels.slice(0, -1));
            setActiveTabIndex(Math.max(activeTabIndex - 1, 0));
        }
    };

    const updateForm = (formIndex, updatedForm) => {
        dispatchForms({
            type: "UPDATE_FORM",
            formIndex: formIndex,
            updatedForm: updatedForm,
        });
    };

    return (
        <>
            {loading ? (
                <div>
                    <div className="loader center">
                        <i className="fa fa-cog fa-spin" />
                    </div>
                </div>
            ) : (
                <div id={"workflows-table"}>
                    <RootContainer>
                        <TabsContainer>
                            <StyledTabs
                                variant="scrollable"
                                scrollButtons="auto"
                                value={activeTabIndex}
                                onChange={(event, newValue) => setActiveTabIndex(newValue)}
                            >
                                {tabLabels.map((label, index) => (
                                    <Tab
                                        key={index}
                                        value={index}
                                        onClick={() => handleTabClick(index)}
                                        label={
                                            <StatusTabLabel
                                                label={label}
                                                status={forms[index]?.data?.status}
                                            />
                                        }
                                    />
                                ))}
                                <Tab
                                    label={tabLabels.length > 0 ? "+" : "CREATE WORKFLOW"}
                                    onClick={addNewForm}
                                />
                            </StyledTabs>
                        </TabsContainer>
                        <FormContainer>
                            {tabLabels.map((label, index) => (
                                <FormBox
                                    key={index}
                                    style={{
                                        display: index === activeTabIndex ? "block" : "none",
                                    }}
                                >
                                    <PullListForm
                                        index={activeTabIndex}
                                        form={forms[activeTabIndex]}
                                        updateForm={updateForm}
                                        pullListFolderId={pullListFolderId}
                                        sampleDisposal={sampleDisposal}
                                        lab={lab}
                                        labLegacyAddress={labLegacyAddress}
                                        couriers={couriers}
                                        isEPApproved={isEPApproved}
                                        onCancel={handleCancelNewForm}
                                    />
                                </FormBox>
                            ))}
                        </FormContainer>
                    </RootContainer>
                </div>
            )}
        </>
    );
};

export default PullListTable;